import React, { useEffect, useMemo, useState } from "react";
import {
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CRow,
  CSpinner,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
  CBadge,
  CFormInput,
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
} from "@coreui/react";
import { CChartBar, CChartDoughnut } from "@coreui/react-chartjs";
import SalesPicker from "src/components/SalesPicker";
import ls from "local-storage";
import { getAll as getAllStores, getUplines } from "src/services/Stores";
import DataTable from "src/components/DataTable";
import { useNavigate } from "react-router-dom";


function StoresPerUpline() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stores, setStores] = useState([]);
  const [search, setSearch] = useState("");
  const [showModalType, setShowModalType] = useState(false);
  const [showModalStore, setShowModalStore] = useState(false);
  const [selectedUplineKey, setSelectedUplineKey] = useState(null);
  const [uplinesMap, setUplinesMap] = useState({}); // id -> name

  const getFilterParams = () => {
    const smd = ls.get("smd");
    const prov = ls.get("prov");
    const city = ls.get("city");

    return {
      smdId: smd?.id && smd.id !== "999" && smd.value !== "all" ? smd.id : "0",
      provinceId: prov?.id && prov.id !== "999" && prov.value !== "all" ? prov.id : "0",
      cityId: city?.id && city.id !== "999" && city.value !== "all" ? city.id : "0",
    };
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const filters = getFilterParams();
      const [res, uplines] = await Promise.all([
        getAllStores(filters),
        getUplines(),
      ]);
      setStores(res?.stores || []);

      const list = (uplines?.data || uplines?.uplines || uplines) || [];
      const mapped = {};
      for (const u of list) {
        const idCandidate = u?.id ?? u?.upline_id ?? u?.uplineId ?? u?.upline_code;
        if (idCandidate === undefined || idCandidate === null) continue;
        const id = String(idCandidate);
        const name = u?.name ?? u?.upline_name ?? u?.uplineName ?? id;
        mapped[id] = name;
      }
      setUplinesMap(mapped);
    } catch (error) {
      console.error("Error fetching stores for upline analytics:", error);
      setStores([]);
      setUplinesMap({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const groupedByUpline = useMemo(() => {
    const groups = {};
    for (const s of stores) {
      const idRaw = s?.upline_name; // Note: API returns ID in this field
      const key = idRaw ? String(idRaw) : "no_upline";
      const displayName = idRaw ? (uplinesMap[String(idRaw)] || `Upline ${idRaw}`) : "Tanpa Upline";
      if (!groups[key]) {
        groups[key] = {
          uplineId: idRaw || null,
          upline: displayName,
          total: 0,
          visited: 0,
          notVisited: 0,
          typeCount: {},
        };
      }
      groups[key].total += 1;
      if (s?.last_visited) groups[key].visited += 1;
      else groups[key].notVisited += 1;
      const t = s?.type || "-";
      groups[key].typeCount[t] = (groups[key].typeCount[t] || 0) + 1;
    }
    return groups;
  }, [stores, uplinesMap]);

  const sortedUplineKeys = useMemo(() => {
    return Object.keys(groupedByUpline).sort(
      (a, b) => groupedByUpline[b].total - groupedByUpline[a].total
    );
  }, [groupedByUpline]);

  const filteredUplineKeys = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return sortedUplineKeys;
    return sortedUplineKeys.filter((id) => groupedByUpline[id]?.upline?.toLowerCase().includes(term));
  }, [sortedUplineKeys, search, groupedByUpline]);

  const TOP_N = 1000;
  const barChartLabels = useMemo(() => {
    if (!filteredUplineKeys.length) return [];
    const topIds = filteredUplineKeys.slice(0, TOP_N);
    const hasOthers = filteredUplineKeys.length > TOP_N;
    const labels = topIds.map((id) => groupedByUpline[id]?.upline || id);
    return hasOthers ? [...labels, "Lainnya"] : labels;
  }, [filteredUplineKeys, groupedByUpline]);

  const barChartData = useMemo(() => {
    if (!filteredUplineKeys.length) return [];
    const topIds = filteredUplineKeys.slice(0, TOP_N);
    const topValues = topIds.map((id) => groupedByUpline[id].total);
    if (filteredUplineKeys.length > TOP_N) {
      const others = filteredUplineKeys.slice(TOP_N);
      const othersSum = others.reduce((sum, id) => sum + groupedByUpline[id].total, 0);
      return [...topValues, othersSum];
    }
    return topValues;
  }, [filteredUplineKeys, groupedByUpline]);

  const topUplineIds = useMemo(() => filteredUplineKeys.slice(0, TOP_N), [filteredUplineKeys]);

  const handleBarClick = (index) => {
    if (index == null) return;
    if (index < topUplineIds.length) {
      const id = topUplineIds[index];
      navigate(`/stores?upline_name=${id}`)
    }
  };

  const handleDonutClick = (upline, tipe) => {
    navigate(`/stores?type=${tipe}&upline_name=${upline}`)
  }

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "80vh" }}>
        <CSpinner color="primary" size="lg" />
      </div>
    );
  }

  return (
    <div>
      <SalesPicker onFilterChange={fetchData} />
      <CRow className="mt-3">
        <CCol lg={6} md={8} sm={12}>
          <CFormInput
            type="text"
            placeholder="Cari upline..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </CCol>
      </CRow>
      <CRow className="mt-3">
        <CCol lg={12}>
          <CCard className="mb-4">
            <CCardHeader>Jumlah Toko per Upline</CCardHeader>
            <CCardBody>
              <CChartBar
                data={{
                  labels: barChartLabels,
                  datasets: [
                    {
                      label: "Total Toko",
                      backgroundColor: "#36A2EB",
                      data: barChartData,
                    },
                  ],
                }}
                options={{
                  indexAxis: "x",
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: { position: "bottom" },
                  },
                  scales: {
                    x: { beginAtZero: true },
                  },
                  onClick: (evt, elements) => {
                    if (elements && elements.length > 0) {
                      const idx = elements[0].index;
                      handleBarClick(idx);
                    }
                  },
                }}
                style={{ minHeight: 300, maxHeight: 400 }}
              />
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      <CRow>
        <CCol lg={12}>
          <CCard className="mb-4">
            <CCardHeader>Ringkasan per Upline</CCardHeader>
            <CCardBody>
              <div style={{ maxHeight: 420, overflowY: "auto" }}>
                <CTable hover responsive>
                <CTableHead>
                  <CTableRow>
                    <CTableHeaderCell>Upline</CTableHeaderCell>
                    <CTableHeaderCell className="text-center">Total</CTableHeaderCell>
                    <CTableHeaderCell className="text-center">Terkunjungi</CTableHeaderCell>
                    <CTableHeaderCell className="text-center">Belum Terkunjungi</CTableHeaderCell>
                    <CTableHeaderCell>Tipe Terbanyak</CTableHeaderCell>
                  </CTableRow>
                </CTableHead>
                <CTableBody>
                  {filteredUplineKeys.map((key) => {
                    const g = groupedByUpline[key];
                    const topType = Object.entries(g.typeCount)
                      .sort((a, b) => b[1] - a[1])
                      .map(([type, count]) => ({ type, count }))[0];
                    return (
                      <CTableRow
                        key={key}
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          setSelectedUplineKey(key);
                          setShowModalType(true);
                        }}
                      >
                        <CTableDataCell>{g.upline}</CTableDataCell>
                        <CTableDataCell className="text-center">
                          <CBadge color="primary">{g.total}</CBadge>
                        </CTableDataCell>
                        <CTableDataCell className="text-center">
                          <CBadge color="success">{g.visited}</CBadge>
                        </CTableDataCell>
                        <CTableDataCell className="text-center">
                          <CBadge color="danger">{g.notVisited}</CBadge>
                        </CTableDataCell>
                        <CTableDataCell>
                          {topType ? (
                            <span>
                              Tipe {topType.type} ({topType.count})
                            </span>
                          ) : (
                            "-"
                          )}
                        </CTableDataCell>
                      </CTableRow>
                    );
                  })}
                </CTableBody>
                </CTable>
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
      <CModal visible={showModalType} onClose={() => setShowModalType(false)} alignment="center" size="lg">
        <CModalHeader onClose={() => setShowModalType(false)}>
          <CModalTitle>Distribusi Tipe - {selectedUplineKey}</CModalTitle>
        </CModalHeader>
        <CModalBody>
          {selectedUplineKey && (
            (() => {
              const g = groupedByUpline[selectedUplineKey];
              const id = Object.keys(g.typeCount);
              const labels = Object.keys(g.typeCount).map((t) => `Tipe ${t}`);
              const data = Object.values(g.typeCount);
              return labels.length ? (
                <div style={{ width: '50%', margin: '0 auto' }}>
                  <CChartDoughnut
                    data={{
                      labels,
                      datasets: [
                        { backgroundColor: ["#4BC0C0", "#36A2EB", "#FFCE56", "#FF6384", "#9966FF", "#FF9F40"], data },
                      ],
                    }}
                    options={{ 
                      maintainAspectRatio: true, 
                      responsive: true,
                      onClick: (evt, elements) => {
                        console.log(elements)
                        if (elements && elements.length > 0) {
                          const index = elements[0].index;
                          const clickedLabel = id[index];
                          handleDonutClick(selectedUplineKey, clickedLabel);
                        }
                      }
                    }}
                  />
                </div>
              ) : (
                <div className="text-muted">Tidak ada data tipe</div>
              );
            })()
          )}
        </CModalBody>
      </CModal>
      {/* <CModal visible={showModalStore} onClose={() => setShowModalStore(false)} alignment="center" size="xl">
        <CModalHeader onClose={() => setShowModalStore(false)}>
          <CModalTitle>
            Daftar Toko · {uplinesMap[selectedUplineKey] || groupedByUpline[selectedUplineKey]?.upline || "Tanpa Upline"}
          </CModalTitle>
        </CModalHeader>
        <CModalBody>
          {selectedUplineKey && (() => {
            const rows = stores
              .filter((s) => String(s?.upline_name || "NO_UPLINE") === String(selectedUplineKey))
              .map((s) => ({
                id: s.id,
                name: s.name,
                type: s.type || "-",
                address: s.address || "-",
                last_visited: s.last_visited ? new Date(s.last_visited).toLocaleDateString() : "-",
              }));

            const columns = [
              { field: "id", headerName: "ID", width: 100 },
              { field: "name", headerName: "Nama Toko", flex: 1, minWidth: 200 },
              { field: "type", headerName: "Tipe", width: 100 },
              { field: "address", headerName: "Alamat", flex: 1, minWidth: 220 },
              { field: "last_visited", headerName: "Terakhir Kunjungan", width: 160 },
            ];

            return (
              <div style={{ marginTop: 4 }}>
                <DataTable columns={columns} rows={rows} loading={loading} autoHeight density="compact" />
              </div>
            );
          })()}
        </CModalBody>
      </CModal> */}
    </div>
  );
}

export default StoresPerUpline;

